import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { BookOpen, ChevronDown } from "lucide-react";

const FALLBACK_TESTIMONIALS = [
  { name: "George Arkhurst", book: "Author of 'Amazing Facts'", quote: "GloriPub took my manuscript from a Word file to a live Amazon listing in weeks. The pipeline view meant I always knew exactly where my book was." },
  { name: "Jonathan Jato", book: "Author of 'The Power of Apology'", quote: "The royalty dashboard changed everything for me. I can see every sale, every marketplace, and withdraw my earnings whenever I want." },
  { name: "Samuel Liadi", book: "Author of 'Your Path to Impact the World'", quote: "A stunning cover, flawless formatting, and a team that answers every message. Publishing finally felt like a partnership." },
];

const links = [
  { to: "/store", label: "Bookstore" },
  { to: "/pricing", label: "Pricing" },
  { to: "/marketing", label: "Marketing" },
  { to: "/helpdesk", label: "Helpdesk" },
  { to: "/support", label: "Support" },
];

const companyLinks = [
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const CompanyMenu = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const isActive = companyLinks.some((l) => l.to === location.pathname);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  return (
    <div ref={ref} className="relative">
      <button data-testid="public-nav-company" onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive || open ? "bg-[#F5F5F7] text-[#0A0A0A]" : "text-[#52525B] hover:text-[#0A0A0A]"}`}>
        Company <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="public-nav-company-menu" className="absolute left-0 top-full mt-2 w-44 bg-white border border-[#E4E4E7] rounded-2xl shadow-lg py-2 overflow-hidden">
          {companyLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`public-nav-${l.label.toLowerCase()}`}
              className={({ isActive: a }) => `block px-5 py-2.5 text-sm transition-colors ${a ? "bg-[#F5F5F7] font-medium text-[#0A0A0A]" : "text-[#52525B] hover:bg-[#F5F5F7] hover:text-[#0A0A0A]"}`}>
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export const PublicLayout = ({ children }) => (
  <div className="min-h-screen bg-white text-[#0A0A0A]">
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E4E4E7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[68px]">
        <Link to="/" data-testid="public-logo" className="flex items-center gap-2 font-bold text-xl font-heading tracking-tight">
          <BookOpen size={24} strokeWidth={2.4} /> GloriPub
        </Link>
        <div className="flex items-center gap-1">
          <nav className="hidden md:flex items-center gap-1 mr-3">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`public-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[#F5F5F7] text-[#0A0A0A]" : "text-[#52525B] hover:text-[#0A0A0A]"}`}>
                {l.label}
              </NavLink>
            ))}
            <CompanyMenu />
          </nav>
          <Link to="/login" data-testid="public-login" className="hidden sm:block px-4 py-2 text-sm font-medium text-[#52525B] hover:text-[#0A0A0A] transition-colors">Log in</Link>
          <Link to="/register" data-testid="public-register"
            className="px-5 py-2.5 text-sm font-medium rounded-full text-white bg-[#0A0A0A] hover:bg-[#27272A] transition-colors active:scale-95">
            Get started
          </Link>
        </div>
      </div>
      <nav className="md:hidden flex overflow-x-auto px-4 pb-2.5 gap-1">
        {[...links, ...companyLinks].map((l) => (
          <NavLink key={l.to} to={l.to} className={({ isActive }) => `px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap ${isActive ? "bg-[#F5F5F7] font-medium" : "text-[#52525B]"}`}>{l.label}</NavLink>
        ))}
      </nav>
    </header>
    {children}
    <footer className="border-t border-[#E4E4E7] bg-[#F5F5F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid sm:grid-cols-3 gap-10">
        <div>
          <p className="flex items-center gap-2 font-bold text-lg font-heading"><BookOpen size={19} /> GloriPub</p>
          <p className="text-sm text-[#52525B] mt-3 max-w-xs leading-relaxed">From manuscript to self publishing — production, sales tracking and royalties, handled with care.</p>
        </div>
        <div className="text-sm space-y-2.5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA]">Explore</p>
          {[...links, ...companyLinks].map((l) => <Link key={l.to} to={l.to} className="block text-[#52525B] hover:text-[#0A0A0A] transition-colors">{l.label}</Link>)}
        </div>
        <div className="text-sm space-y-2.5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA]">Authors</p>
          <Link to="/register" className="block text-[#52525B] hover:text-[#0A0A0A] transition-colors">Submit a manuscript</Link>
          <Link to="/login" className="block text-[#52525B] hover:text-[#0A0A0A] transition-colors">Author dashboard</Link>
        </div>
      </div>
      <p className="text-center text-xs text-[#A1A1AA] pb-10">© 2026 GloriPub — Self Publishing Platform</p>
    </footer>
  </div>
);

export const SectionHeading = ({ eyebrow, title, sub }) => (
  <div className="mb-12">
    {eyebrow && <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-4">{eyebrow}</p>}
    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight font-heading">{title}</h2>
    {sub && <p className="text-[#52525B] mt-4 max-w-2xl leading-relaxed">{sub}</p>}
  </div>
);

export const Testimonials = () => {
  const [items, setItems] = useState(FALLBACK_TESTIMONIALS);
  useEffect(() => {
    api.get("/testimonials").then((r) => { if (r.data?.length) setItems(r.data); }).catch(() => {});
  }, []);
  return (
  <div className="grid md:grid-cols-3 gap-8" data-testid="testimonials-grid">
    {items.map((t) => (
      <figure key={t.name} data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
        className="bg-white border border-[#E4E4E7] rounded-3xl p-8 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
        <p className="leading-relaxed text-[15px]">“{t.quote}”</p>
        <figcaption className="mt-8">
          <p className="font-semibold font-heading">{t.name}</p>
          <p className="text-xs text-[#A1A1AA] mt-0.5">{t.book}</p>
        </figcaption>
      </figure>
    ))}
  </div>
  );
};
